import { _isNil, _notNil } from '@/littledash';
import type Handsontable from 'handsontable-v16';
import type { HotTableProps } from './HotTableV16';

// Custom editors v16
import { ColorEditorV16 } from './editors-v16/ColorEditorV16';
import { ExponentialEditorV16 } from './editors-v16/ExponentialEditorV16';
import { DateEditorWrapperV16 } from './editors-v16/DateEditorWrapperV16';
import { DateTimeEditorV16 } from './editors-v16/DateTimeEditorV16';
import { MultiSelectEditorV16 } from './editors-v16/MultiSelectEditorV16';

import type { CellType } from 'handsontable-v16/cellTypes';
import type { ReactNode } from 'react';
import type { CustomColumnSettingsV16, CustomValidationV16, SpreadSheetErrorV16 } from './SpreadsheetV16.model';

type ColumnConfig = Record<string, unknown>;

/**
 * Convert GridSettings to simplified HotTableV16 props
 */
export function convertSettingsToHotTableV16Props(settings: Handsontable.GridSettings): Partial<HotTableProps> {
  const hotTableProps: Partial<HotTableProps> = {
    // Headers
    colHeaders: settings.colHeaders,
    rowHeaders: settings.rowHeaders,

    // Sorting
    columnSorting: settings.columnSorting,

    // Sizing
    width: settings.width,
    height: settings.height,
    rowHeights: settings.rowHeights,
    colWidths: settings.colWidths,

    // Behavior
    minSpareRows: settings.minSpareRows,
    minSpareCols: settings.minSpareCols,
    maxRows: settings.maxRows,
    maxCols: settings.maxCols,
    manualColumnResize: settings.manualColumnResize,
    manualRowResize: settings.manualRowResize,
    outsideClickDeselects: settings.outsideClickDeselects,

    // Context menu
    contextMenu: settings.contextMenu,

    // Hidden columns plugin
    hiddenColumns: settings.hiddenColumns,

    // Styling
    className: settings.className as string | undefined,

    // License
    licenseKey: settings.licenseKey,
  };

  // Columns - process to add v16 custom editors
  if (Array.isArray(settings.columns)) {
    hotTableProps.columns = settings.columns.map((column) => {
      const col = column as Handsontable.ColumnSettings & { customEditor?: unknown };
      const columnConfig: ColumnConfig = { ...col };
      const { type, customEditor } = col;

      // Auto-map v16 custom editors based on type
      let editor = customEditor;
      const isEditorDefined = _notNil(editor);
      if (isEditorDefined) {
        if (type === 'color-v16') {
          editor = ColorEditorV16;
        } else if (type === 'numeric-expo-v16') {
          editor = ExponentialEditorV16;
        } else if (type === 'date-wrapper-v16') {
          editor = DateEditorWrapperV16;
        } else if (type === 'datetime-v16') {
          editor = DateTimeEditorV16;
        } else if (type === 'multi-select-v16') {
          editor = MultiSelectEditorV16;
        }
        void type; // Temporarily mark as used
      }

      if (_notNil(editor)) {
        columnConfig.editor = editor;
      }

      return columnConfig;
    }) as Handsontable.ColumnSettings[];
  } else {
    hotTableProps.columns = settings.columns;
  }

  return hotTableProps;
}

/**
 * Extract column settings that should be passed to HotColumn
 *
 * @param columnSettings - column settings
 * @returns v16 compatible column settings
 */
export function convertColumnSettingsV16(columnSettings: ColumnConfig): Partial<ColumnConfig> {
  // Most column settings are compatible
  return columnSettings;
}

export const convertSettingsToV16Props = convertSettingsToHotTableV16Props;

export type SpreadSheetErrorMessageV16 = (value: ReactNode, rowData?: unknown) => string;
export const spreadSheetValidationTooltipV16 = 'There are invalid cells';

export const formatValidationErrorsV16 = (errors: Record<string, SpreadSheetErrorV16>): Array<string> =>
  Object.keys(errors).map((column: string) => createValidationErrorV16(errors[column], ''));

export const createValidationErrorV16 = (
  error: SpreadSheetErrorV16,
  type?: string,
  customMessage?: SpreadSheetErrorMessageV16,
  rowData?: unknown
): string => {
  if ((_isNil(error.value) || error.value === '') && _isNil(customMessage)) {
    return 'Required Field';
  } else {
    return (
      customMessage?.(error.value, rowData) ?? errorCellTypesV16[type as CellType]?.(error.value) ?? `Is not valid`
    );
  }
};

export const buildCustomValidatorsV16 = (
  columns: Array<CustomColumnSettingsV16>
): Record<string, CustomValidationV16> =>
  columns.reduce<Record<string, CustomValidationV16>>(
    (acc, { data, validationErrorMessage, linkedValidationColumns }) => {
      if (_notNil(data)) {
        return { ...acc, [data.toString()]: { validationErrorMessage, linkedValidationColumns } };
      } else {
        return acc;
      }
    },
    {}
  );

type CustomCellTypesV16 = 'numeric-expo-v16' | 'multi-select-v16' | 'date-wrapper-v16' | 'color-v16' | 'datetime-v16';

const errorCellTypesV16: Record<CellType | CustomCellTypesV16, SpreadSheetErrorMessageV16> = {
  dropdown: () => `Is not a valid option`,
  select: () => `Is not a valid option`,
  date: () => `Must be a valid date`,
  numeric: (value) => `${value ?? 'Value'} must be a number`,
  time: () => `Must be a valid time`,
  autocomplete: () => `Is not valid`,
  handsontable: () => `Is not valid`,
  password: () => `Is not valid`,
  text: () => `Must have a max length of 255 characters`,
  checkbox: () => `Is not valid`,
  'numeric-expo-v16': () => `Value must be a number`,
  'multi-select-v16': () => `Is not a valid option`,
  'date-wrapper-v16': () => `Must be a valid YYYY-MM-DD date`,
  'color-v16': () => `Must be a valid hex color`,
  'datetime-v16': () => `Must be a valid date and time`,
};
